import {
  CheckCircleFilled,
  DownloadOutlined,
  FileDoneOutlined,
  LeftCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Col,
  Divider,
  Form,
  Modal,
  Row,
  Space,
  Spin,
  Tabs,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import SignatureCanvas from "react-signature-canvas";
import * as yup from "yup";
import "./SignaturePage.css";

import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.entry";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { CustomInput } from "../../../../../shared/formFields/CustomInput";
import { navigateToPreviousPage } from "../../../../../store/aviationForm/aviationFormSlice";
import { useSaveApplicationMutation } from "../../../../../store/baseApi";
import {
  clearSignature,
  setModalConsent,
} from "../../../../../store/signature/signatureSlice";
import {
  fetchPreSignaturePdf,
  fetchSignedPdf,
} from "../../../../../store/signature/signatureThunks";
import ConsentModal from "./ConsentModal";
import { signatureFonts } from "./signatureFonts";

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

const ORIG_PDF_WIDTH = 819;
const ORIG_PDF_HEIGHT = 1058;
const SIGN_X_ORIG = 536;
const SIGN_Y_ORIG = 955;
const MIN_SCALE = 1.0;
const MAX_SCALE = 1.4;
const NAME_MAX = 40;

const schema = yup.object().shape({
  main_owner_name: yup
    .string()
    .trim()
    .required("Full name is required")
    .max(NAME_MAX, `Name can be max ${NAME_MAX} characters`),
});

export default function SignaturePage() {
  const data = useSelector((state) => state.aviationForm.data);
  const { applicationId } = useParams();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: data,
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const { url, isLoading, modalConsent, pdfName, error } = useSelector(
    (s) => s.signature
  );

  const [isModalVisible, setModalVisible] = useState(false);

  const mainOwnerName = watch("main_owner_name");
  const [saveApplication] = useSaveApplicationMutation();

  const dispatch = useDispatch();
  const [tabKey, setTab] = useState("typed");
  const [selectedFontId, setSelectedFontId] = useState(signatureFonts[0].id);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const [signature, setSignature] = useState(null);

  const padRef = useRef(null);
  const topPageRef = useRef(null);
  const bottomPageRef = useRef(null);

  const [pages, setPages] = useState(null);
  const [scale, setScale] = useState(MIN_SCALE);
  const [pageIdx, setPage] = useState(1);
  const pdfBoxRef = useRef(null);

  const nameEmpty = mainOwnerName.trim() === "";
  const drawNeeded = tabKey === "drawn" && !hasDrawn;
  const reviewDisabled = nameEmpty || drawNeeded;

  const tagPos = useMemo(() => {
    const w = ORIG_PDF_WIDTH * scale;
    return {
      left: `calc(50% - ${w / 2}px + ${SIGN_X_ORIG * scale}px)`,
      top: SIGN_Y_ORIG * scale,
    };
  }, [scale]);

  const zoomIn = () =>
    setScale((s) => Math.min(MAX_SCALE, +(s + 0.1).toFixed(2)));
  const zoomOut = () =>
    setScale((s) => Math.max(MIN_SCALE, +(s - 0.1).toFixed(2)));

  const clearDraw = () => {
    padRef.current?.clear();
    setHasDrawn(false);
  };

  useEffect(() => {
    dispatch(clearSignature());
    return () => {
      dispatch(clearSignature());
    };
  }, [dispatch]);

  useEffect(() => {
    if (applicationId) {
      dispatch(fetchPreSignaturePdf({ id: applicationId }));
    }
  }, [dispatch, applicationId]);

  useEffect(() => {
    setPages(null);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  useEffect(() => {
    if (!pages) return;
    const viewer = pdfBoxRef.current;
    const pagePx = ORIG_PDF_HEIGHT * scale + 48;
    const onScroll = () =>
      setPage(
        Math.min(pages, Math.max(1, Math.round(viewer.scrollTop / pagePx) + 1))
      );
    viewer.addEventListener("scroll", onScroll);
    return () => viewer.removeEventListener("scroll", onScroll);
  }, [pages, scale]);

  const finalizeSignature = () => {
    if (reviewDisabled) return;
    let sig;

    if (tabKey === "typed") {
      const fontObj = signatureFonts.find((f) => f.id === selectedFontId);
      sig = {
        signature_type: "typed",
        signature_data: { font: fontObj.apiValue, name: mainOwnerName.trim() },
      };
    } else {
      sig = {
        signature_type: "drawn",
        signature_data: {
          data_url: padRef.current
            ?.getTrimmedCanvas()
            .toDataURL("image/png", 0.8),
        },
      };
    }
    setSignature(sig);
    setFinalized(true);

    scrollToBottom();
  };

  const changeSignature = () => {
    pdfBoxRef.current?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    topPageRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
    if (pdfName === "signed") {
      dispatch(fetchPreSignaturePdf({ id: applicationId }));
    }
    setFinalized(false);
    clearDraw();
  };

  const scrollToBottom = () => {
    pdfBoxRef.current?.scrollTo({
      top: pdfBoxRef.current.scrollHeight,
      behavior: "smooth",
    });

    bottomPageRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <Row justify="center" style={{ padding: 24 }}>
      <Col span={24} style={{ maxWidth: 1600, minWidth: 300 }}>
        <h2 className="ds-header" ref={topPageRef}>
          Create Your Signature
        </h2>
        <Divider className="ds-divider" />
        <h3 className="ds-subheader">Confirm your name and signature</h3>
        <p className="ds-helper">
          Please provide your full name and create your electronic signature
          below.
        </p>
        <Form
          name="signature_form"
          layout="vertical"
          autoComplete="off"
          onFinish={handleSubmit(() => {})}
        >
          <CustomInput
            formItem={{
              required: true,
              label: "Full Name",
              style: { marginBottom: 12 },
              validateStatus: errors.main_owner_name ? "error" : "",
              help: errors.main_owner_name?.message,
            }}
            control={control}
            name="main_owner_name"
            placeholder="Enter your full name"
            maxLength={NAME_MAX}
            disabled={finalized}
            size="small"
          />

          {!finalized && (
            <>
              <h3 className="ds-subheader" style={{ marginTop: 32 }}>
                Create your electronic signature
              </h3>

              <Tabs
                className="sig-tabs"
                activeKey={tabKey}
                onChange={(k) => setTab(k)}
                items={[
                  {
                    key: "typed",
                    label: "Select Style",
                    disabled: nameEmpty,
                    children: (
                      <>
                        <div className="typed-grid">
                          {signatureFonts.map((f) => (
                            <Button
                              key={f.id}
                              className={`style-box ${
                                f.id === selectedFontId ? "active" : ""
                              }`}
                              type="text"
                              disabled={nameEmpty}
                              onClick={() => setSelectedFontId(f.id)}
                              style={{ fontFamily: f.cssFamily }}
                            >
                              {f.id === selectedFontId && (
                                <span className="tick" />
                              )}
                              {mainOwnerName || "Signature"}
                            </Button>
                          ))}
                        </div>
                        <p className="legal">
                          By typing my name above, I adopt this electronic
                          signature as my legal signature under applicable law.
                        </p>
                      </>
                    ),
                  },
                  {
                    key: "drawn",
                    label: "Draw Signature",
                    disabled: nameEmpty,
                    children: (
                      <>
                        <div className="draw-head" style={{ width: 400 }}>
                          <span className="draw-title">
                            DRAW YOUR SIGNATURE
                          </span>
                          <Button
                            type="link"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={clearDraw}
                            disabled={nameEmpty}
                            className="clear-btn"
                          >
                            Clear
                          </Button>
                        </div>

                        <div
                          className="canvas-wrap"
                          style={{
                            pointerEvents: nameEmpty ? "none" : "auto",
                            opacity: nameEmpty ? 0.4 : 1,
                          }}
                        >
                          <SignatureCanvas
                            ref={padRef}
                            penColor="#000"
                            canvasProps={{
                              width: 400,
                              height: 160,
                              className: "sig-canvas",
                            }}
                            onEnd={() => setHasDrawn(true)}
                          />
                        </div>

                        <p className="legal">
                          By drawing my signature above, I adopt it as my legal
                          signature under applicable law.
                        </p>
                      </>
                    ),
                  },
                ]}
              />

              <Button
                type="default"
                size="medium"
                icon={<FileDoneOutlined />}
                className="review-btn"
                onClick={finalizeSignature}
                disabled={reviewDisabled}
              >
                Review and Sign
              </Button>
            </>
          )}

          {finalized && (
            <>
              <h3 className="ds-subheader" style={{ marginTop: 32 }}>
                This is what your signature will look like
              </h3>
              {signature.signature_type === "typed" ? (
                <div
                  className="style-box active"
                  style={{
                    fontFamily: signatureFonts.find(
                      (f) => f.id === selectedFontId
                    ).cssFamily,
                    pointerEvents: "none",
                  }}
                >
                  {signature.signature_data.name}
                </div>
              ) : (
                <img
                  src={signature.signature_data.data_url}
                  alt="Drawn signature"
                  className="sig-summary-img"
                />
              )}
              <div style={{ marginTop: 24, marginBottom: 40 }}>
                <Button
                  type="link"
                  icon={<ReloadOutlined />}
                  onClick={changeSignature}
                  className="change-btn"
                >
                  Change my signature
                </Button>
              </div>
            </>
          )}
        </Form>
        <h2 className="ds-header">Document Preview</h2>
        <Divider className="ds-divider" />
        <div className="pdf-toolbar">
          <Space>
            <Button
              size="small"
              icon={<MinusOutlined />}
              onClick={zoomOut}
              disabled={scale === MIN_SCALE || !finalized}
            />
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={zoomIn}
              disabled={scale === MAX_SCALE || !finalized}
            />
            <span className="page-indicator">
              {Math.round(scale * 100)} % | Page {pageIdx}/{pages || "-"}
            </span>
          </Space>
        </div>
        <Spin spinning={!finalized} indicator={<div />}>
          <Spin spinning={isLoading}>
            <div ref={pdfBoxRef} className="pdf-box">
              <Document
                file={url}
                options={{ disableFontFace: true }}
                noData={
                  isLoading ? (
                    <div className="pdf-loading">
                      <Spin />
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: 40 }}>
                      No document found
                    </div>
                  )
                }
                error={
                  isLoading ? (
                    <div className="pdf-loading"></div>
                  ) : (
                    <div style={{ color: "red" }}>
                      {error ?? "Something went wrong while loading the PDF"}
                    </div>
                  )
                }
                onLoadSuccess={({ numPages }) => setPages(numPages)}
                loading={
                  <div className="pdf-loading">
                    <Spin />
                  </div>
                }
              >
                {Array.from({ length: pages || 0 }, (_, i) => (
                  <div key={i} className="pdf-page-wrapper">
                    <Page
                      pageNumber={i + 1}
                      width={ORIG_PDF_WIDTH * scale}
                      renderTextLayer={false}
                      loading={<div className="pdf-loading"></div>}
                      onRenderSuccess={() => {
                        if (i == 1 && pdfName === "signed") {
                          scrollToBottom();
                        }
                      }}
                    />
                    {i + 1 === pages && pdfName !== "signed" && (
                      <div
                        className="sign-here-card"
                        style={tagPos}
                        onClick={() => {
                          setModalVisible(true);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="sign-here-banner">REQUIRED</div>
                        <DownloadOutlined />
                        <span className="sign-here-caption">Sign here</span>
                      </div>
                    )}
                  </div>
                ))}
              </Document>
            </div>
          </Spin>
        </Spin>
        <div style={{ textAlign: "right", marginTop: 32 }} ref={bottomPageRef}>
          <Row justify="end" gutter={[8, 8]}>
            <Col>
              <Button
                type="default"
                size="medium"
                onClick={() => {
                  dispatch(navigateToPreviousPage());
                }}
              >
                <LeftCircleOutlined /> Previous
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                size="medium"
                disabled={pdfName !== "signed"}
                onClick={() => {
                  saveApplication({
                    content: { ...data, step: 8 },
                  });
                }}
              >
                Submit my application
                <CheckCircleFilled />
              </Button>
            </Col>
          </Row>
        </div>

        <Modal
          title="Electronic Signature Consent"
          open={isModalVisible}
          okText="Accept"
          okButtonProps={{ disabled: !modalConsent }}
          cancelText="Cancel"
          onOk={() => {
            setModalVisible(false);

            dispatch(
              fetchSignedPdf({
                id: applicationId,
                signature_type: signature.signature_type,
                signature_data: signature.signature_data,
              })
            );
          }}
          onCancel={() => setModalVisible(false)}
          afterClose={() => dispatch(setModalConsent(false))}
        >
          <ConsentModal />
        </Modal>
      </Col>
    </Row>
  );
}
